<template>
  <section class="mipDiagnosisList">
    <div class="mipDiagnosisHeader">
      <div>
        <strong>Diagnósticos da inspeção</strong>
        <small>Selecione o tipo e depois informe o alvo monitorado.</small>
      </div>
      <button type="button" class="btnAddDiagnosis" @click="addDraftRow">
        + Adicionar novo diagnóstico
      </button>
    </div>

    <div v-if="!persistedRows.length && !currentDrafts.length" class="mipEmptyState">
      <span>Nenhum diagnóstico adicionado.</span>
      <small>Use o botão acima para registrar uma praga, doença ou planta daninha.</small>
    </div>

    <div v-else class="mipRows">
      <article
        v-for="row in persistedRows"
        :key="`${row.type}-${row.target.id}`"
        class="mipDiagnosisRow"
      >
        <div class="mipTypeCell">
          <span class="mipTypeBadge" :class="`type-${row.type}`">
            {{ typeLabel(row.type) }}
          </span>
        </div>

        <div class="mipTargetCell">
          <b>{{ row.target.nome }}</b>
          <small>{{ row.target.nomeCientifico || fallbackScientificName(row.type) }}</small>
        </div>

        <div v-if="row.type === 'praga'" class="mipAssessmentCell">
          <span class="fieldLabel">Média observada</span>
          <div class="inlineCounter">
            <button type="button" @click="decrement(row.target)">−</button>
            <input v-model.number="row.target.quantidade" type="number" min="0" step="0.5" />
            <small>{{ row.target.unidade }}</small>
            <button type="button" @click="increment(row.target)">+</button>
          </div>
        </div>

        <div v-else-if="row.type === 'doenca'" class="mipAssessmentCell">
          <span class="fieldLabel">Severidade visual</span>
          <select v-model="row.target.status">
            <option v-for="level in infestationLevels" :key="level" :value="level">
              {{ level }}
            </option>
          </select>
        </div>

        <div v-else class="mipAssessmentCell weedInlineAssessment">
          <label class="identifiedToggle">
            <input v-model="row.target.identificado" type="checkbox" />
            <span>{{ row.target.identificado ? "Identificada" : "Ausente" }}</span>
          </label>
          <label>
            <span class="fieldLabel">Cobertura (%)</span>
            <input
              v-model.number="row.target.cobertura"
              type="number"
              min="0"
              max="100"
              :disabled="!row.target.identificado"
            />
          </label>
        </div>

        <div class="mipObservationCell">
          <span class="fieldLabel">Observação</span>
          <input
            v-model.trim="row.target.observacao"
            :placeholder="observationPlaceholder(row.type)"
          />
        </div>

        <button
          type="button"
          class="btnRemoveDiagnosis"
          title="Remover diagnóstico"
          @click="removeTarget(row.type, row.target.id)"
        >
          ×
        </button>
      </article>

      <article v-for="draft in currentDrafts" :key="draft.id" class="mipDiagnosisRow mipDraftRow">
        <label class="mipDraftField mipTypeSelect">
          <span class="fieldLabel">Tipo</span>
          <select
            :value="draft.type"
            @change="changeDraftType(draft, $event.target.value)"
          >
            <option value="">Selecione o tipo</option>
            <option value="praga">Praga</option>
            <option value="doenca">Doença</option>
            <option value="daninha">Planta daninha</option>
          </select>
        </label>

        <template v-if="draft.type">
          <div class="mipSourceChoice">
            <button
              type="button"
              :class="{ active: draft.source === 'catalog' }"
              @click="setDraftSource(draft, 'catalog')"
            >
              Lista rápida
            </button>
            <button
              type="button"
              :class="{ active: draft.source === 'compendium' }"
              @click="setDraftSource(draft, 'compendium')"
            >
              Buscar no Compêndio
            </button>
          </div>

          <label class="mipDraftField mipTargetSelect">
            <span class="fieldLabel">Diagnóstico</span>
            <select
              v-if="draft.source === 'catalog'"
              :key="`${draft.id}-${draft.type}-catalog`"
              v-model="draft.catalogId"
            >
              <option value="">Selecione o diagnóstico</option>
              <option
                v-for="item in availableCatalogTargets(draft.type)"
                :key="item.id"
                :value="item.id"
              >
                {{ problemOptionLabel(item) }}
              </option>
            </select>
            <select
              v-else
              :key="`${draft.id}-${draft.type}-compendium`"
              v-model="draft.problemId"
              :disabled="loading[draft.type]"
            >
              <option value="">
                {{ loading[draft.type] ? "Carregando Compêndio..." : "Selecione no Compêndio" }}
              </option>
              <option
                v-for="item in availableCompendiumTargets(draft.type)"
                :key="item.id"
                :value="item.id"
              >
                {{ problemOptionLabel(item) }}
              </option>
            </select>
          </label>

          <button type="button" class="btnConfirmDiagnosis" @click="confirmDraft(draft)">
            Adicionar
          </button>
        </template>

        <button
          type="button"
          class="btnRemoveDiagnosis"
          title="Cancelar nova linha"
          @click="removeDraftRow(draft.id)"
        >
          ×
        </button>
      </article>
    </div>
  </section>
</template>

<script>
import serviceCompendio from "../../services/serviceCompendio.js";

export default {
  props: {
    diagnosis: { type: Object, required: true },
    catalogs: { type: Object, required: true },
    infestationLevels: { type: Array, required: true },
  },
  data() {
    return {
      draftsByDiagnosis: {},
      compendiumTargets: { praga: [], doenca: [], daninha: [] },
      loadedCompendium: { praga: false, doenca: false, daninha: false },
      loading: { praga: false, doenca: false, daninha: false },
    };
  },
  computed: {
    currentDrafts() {
      return this.draftsByDiagnosis[this.diagnosis.id] || [];
    },
    persistedRows() {
      return [
        ...(this.diagnosis.alvosPragas || []).map((target) => ({ type: "praga", target })),
        ...(this.diagnosis.alvosDoencas || []).map((target) => ({ type: "doenca", target })),
        ...(this.diagnosis.alvosDaninhas || []).map((target) => ({ type: "daninha", target })),
      ];
    },
  },
  methods: {
    typeLabel(type) {
      return { praga: "Praga", doenca: "Doença", daninha: "Planta daninha" }[type];
    },
    collectionName(type) {
      return { praga: "alvosPragas", doenca: "alvosDoencas", daninha: "alvosDaninhas" }[type];
    },
    fallbackScientificName(type) {
      return type === "doenca" ? "Agente causal não informado" : "Nome científico não informado";
    },
    observationPlaceholder(type) {
      return {
        praga: "Observação do alvo (opcional)",
        doenca: "Sintomas, distribuição ou incidência",
        daninha: "Estádio, distribuição ou reboleiras",
      }[type];
    },
    problemOptionLabel(problem) {
      return problem.nomeCientifico
        ? `${problem.nome} (${problem.nomeCientifico})`
        : problem.nome;
    },
    addDraftRow() {
      const diagnosisId = this.diagnosis.id;
      if (!this.draftsByDiagnosis[diagnosisId]) {
        this.$set(this.draftsByDiagnosis, diagnosisId, []);
      }
      this.draftsByDiagnosis[diagnosisId].push({
        id: `draft-${Date.now()}-${Math.random().toString(16).slice(2)}`,
        type: "",
        source: "catalog",
        catalogId: "",
        problemId: "",
      });
    },
    removeDraftRow(id) {
      const index = this.currentDrafts.findIndex((draft) => draft.id === id);
      if (index >= 0) this.currentDrafts.splice(index, 1);
    },
    changeDraftType(draft, type) {
      this.$set(draft, "type", type);
      this.$set(draft, "source", "catalog");
      this.$set(draft, "catalogId", "");
      this.$set(draft, "problemId", "");
    },
    async setDraftSource(draft, source) {
      draft.source = source;
      draft.catalogId = "";
      draft.problemId = "";
      if (source === "compendium") await this.loadCompendiumProblems(draft.type);
    },
    selectedTargets(type) {
      return this.diagnosis[this.collectionName(type)] || [];
    },
    availableCatalogTargets(type) {
      const selectedIds = new Set(this.selectedTargets(type).map((item) => String(item.catalogId)));
      const selectedNames = new Set(
        this.selectedTargets(type).map((item) => String(item.nome || "").trim().toLocaleLowerCase())
      );
      return (this.catalogs[type] || []).filter(
        (item) =>
          !selectedIds.has(String(item.id)) &&
          !selectedNames.has(String(item.nome || "").trim().toLocaleLowerCase())
      );
    },
    availableCompendiumTargets(type) {
      const selectedIds = new Set(this.selectedTargets(type).map((item) => String(item.catalogId)));
      const selectedNames = new Set(
        this.selectedTargets(type).map((item) => String(item.nome || "").trim().toLocaleLowerCase())
      );
      return this.compendiumTargets[type].filter(
        (item) =>
          !selectedIds.has(String(item.catalogId)) &&
          !selectedNames.has(String(item.nome || "").trim().toLocaleLowerCase())
      );
    },
    async loadCompendiumProblems(type) {
      if (this.loadedCompendium[type] || this.loading[type]) return;
      const categoryIds = { doenca: 1, praga: 2, daninha: 3 };
      this.loading[type] = true;
      try {
        const result = await serviceCompendio.findAllProblems(categoryIds[type]);
        const problems = Array.isArray(result) ? result : result?.data || [];
        this.compendiumTargets[type] = problems.map((problem) => ({
          id: String(problem.id),
          catalogId: `compendio-${problem.id}`,
          nome: problem.nomeComum || problem.nomeCientifico,
          nomeCientifico: problem.nomeCientifico || "",
          unidade: type === "praga" ? "indivíduos/ponto" : "",
        }));
        this.loadedCompendium[type] = true;
      } catch (error) {
        this.$toast.open({
          message: "Não foi possível carregar os problemas do Compêndio",
          type: "warning",
        });
      } finally {
        this.loading[type] = false;
      }
    },
    createTarget(type, target) {
      const base = {
        id: `mip-${type}-${Date.now()}-${Math.random().toString(16).slice(2)}`,
        catalogId: target.catalogId || null,
        nome: target.nome,
        nomeCientifico: target.nomeCientifico || "",
        observacao: "",
      };
      if (type === "praga") {
        return { ...base, quantidade: 0, unidade: target.unidade || "indivíduos/ponto" };
      }
      if (type === "doenca") return { ...base, status: "Ausente" };
      return { ...base, identificado: false, cobertura: 0 };
    },
    confirmDraft(draft) {
      const source = draft.source === "compendium"
        ? this.compendiumTargets[draft.type]
        : this.catalogs[draft.type];
      const selectedId = draft.source === "compendium" ? draft.problemId : draft.catalogId;
      const target = (source || []).find((item) => String(item.id) === String(selectedId));
      if (!target) {
        this.$toast.open({ message: "Selecione um diagnóstico para adicionar", type: "warning" });
        return;
      }
      const collection = this.diagnosis[this.collectionName(draft.type)];
      collection.push(
        this.createTarget(draft.type, {
          ...target,
          catalogId: draft.source === "compendium" ? target.catalogId : target.id,
        })
      );
      this.removeDraftRow(draft.id);
    },
    removeTarget(type, targetId) {
      const collection = this.diagnosis[this.collectionName(type)];
      const index = collection.findIndex((item) => item.id === targetId);
      if (index >= 0) collection.splice(index, 1);
    },
    increment(target) {
      target.quantidade = Number(target.quantidade || 0) + 0.5;
    },
    decrement(target) {
      target.quantidade = Math.max(0, Number(target.quantidade || 0) - 0.5);
    },
  },
};
</script>

<style scoped>
.mipDiagnosisList {
  overflow: hidden;
  margin-top: 14px;
  border: 1px solid #d7e5e0;
  border-radius: 10px;
  background: #fff;
}
.mipDiagnosisHeader {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 13px 14px;
  border-bottom: 1px solid #e0ebe7;
  background: #f6faf8;
}
.mipDiagnosisHeader > div { display: flex; flex-direction: column; gap: 2px; }
.mipDiagnosisHeader strong { color: #263f37; font-size: 13px; text-transform: uppercase; }
.mipDiagnosisHeader small, .mipEmptyState small { color: #71837d; font-size: 11px; }
.btnAddDiagnosis, .btnConfirmDiagnosis {
  height: 34px;
  padding: 0 13px;
  border: 1px solid #006256;
  border-radius: 6px;
  color: #fff;
  background: #006256;
  font-size: 11px;
  font-weight: 700;
  cursor: pointer;
}
.mipEmptyState { display: flex; flex-direction: column; align-items: center; gap: 3px; padding: 30px 16px; color: #536a62; font-size: 12px; }
.mipRows { display: flex; flex-direction: column; }
.mipDiagnosisRow {
  display: grid;
  grid-template-columns: 120px minmax(160px, 1.2fr) minmax(180px, 1fr) minmax(180px, 1fr) 30px;
  align-items: center;
  gap: 12px;
  min-height: 72px;
  padding: 10px 12px;
  border-bottom: 1px solid #e7efec;
}
.mipDiagnosisRow:last-child { border-bottom: 0; }
.mipDiagnosisRow:hover { background: #fbfdfc; }
.mipTypeBadge { display: inline-flex; padding: 5px 8px; border-radius: 999px; font-size: 10px; font-weight: 700; white-space: nowrap; }
.type-praga { color: #39554e; background: #e9f0ee; }
.type-doenca { color: #006256; background: #e2f1ed; }
.type-daninha { color: #536b31; background: #edf3e4; }
.mipTargetCell { display: flex; flex-direction: column; min-width: 0; }
.mipTargetCell b { overflow: hidden; color: #294139; font-size: 12px; text-overflow: ellipsis; white-space: nowrap; }
.mipTargetCell small { overflow: hidden; margin-top: 2px; color: #7a8c86; font-size: 10px; font-style: italic; text-overflow: ellipsis; white-space: nowrap; }
.fieldLabel { display: block; margin-bottom: 4px; color: #63766f; font-size: 9px; font-weight: 700; text-transform: uppercase; }
.mipAssessmentCell select, .mipAssessmentCell input, .mipObservationCell input, .mipDraftField select {
  width: 100%; height: 34px; padding: 6px 8px; border: 1px solid #cbdad5; border-radius: 6px; outline: none; color: #344b44; background: #fff; font-size: 11px;
}
.mipAssessmentCell select:focus, .mipAssessmentCell input:focus, .mipObservationCell input:focus, .mipDraftField select:focus { border-color: #006256; box-shadow: 0 0 0 3px rgba(0, 98, 86, 0.07); }
.inlineCounter { display: grid; grid-template-columns: 28px 55px minmax(65px, 1fr) 28px; align-items: center; overflow: hidden; border: 1px solid #cbdad5; border-radius: 6px; }
.inlineCounter button { height: 32px; border: 0; color: #006256; background: #edf5f2; font-weight: 700; cursor: pointer; }
.inlineCounter input { height: 32px; padding: 3px; border: 0; text-align: center; }
.inlineCounter small { overflow: hidden; color: #6d7e78; font-size: 9px; text-overflow: ellipsis; white-space: nowrap; }
.weedInlineAssessment { display: grid; grid-template-columns: 1fr 90px; align-items: end; gap: 8px; }
.identifiedToggle { display: flex; align-items: center; gap: 6px; height: 34px; margin: 0; color: #3e554e; font-size: 11px; }
.identifiedToggle input { width: auto; height: auto; }
.mipObservationCell { min-width: 0; }
.btnRemoveDiagnosis { display: grid; place-items: center; width: 28px; height: 28px; padding: 0; border: 0; border-radius: 5px; color: #a7443b; background: #fff1ef; font-size: 17px; cursor: pointer; }
.mipDraftRow { grid-template-columns: 150px minmax(210px, 1fr) minmax(220px, 1.2fr) auto 30px; background: #f9fcfb; }
.mipSourceChoice { display: grid; grid-template-columns: 1fr 1fr; overflow: hidden; border: 1px solid #bfd3cc; border-radius: 6px; }
.mipSourceChoice button { min-height: 34px; padding: 4px 7px; border: 0; color: #526a62; background: #fff; font-size: 9px; font-weight: 700; cursor: pointer; }
.mipSourceChoice button.active { color: #fff; background: #567c70; }
@media (max-width: 1100px) {
  .mipDiagnosisRow { grid-template-columns: 105px 1fr 1fr 30px; }
  .mipObservationCell { grid-column: 2 / 4; }
  .mipDraftRow { grid-template-columns: 140px 1fr 1fr 30px; }
  .mipDraftRow .btnConfirmDiagnosis { grid-column: 3; }
}
@media (max-width: 700px) {
  .mipDiagnosisHeader { align-items: stretch; flex-direction: column; }
  .mipDiagnosisRow, .mipDraftRow { grid-template-columns: 1fr 30px; gap: 9px; padding: 13px 12px; }
  .mipTypeCell, .mipTargetCell, .mipAssessmentCell, .mipObservationCell, .mipDraftField, .mipSourceChoice, .mipDraftRow .btnConfirmDiagnosis { grid-column: 1; }
  .btnRemoveDiagnosis { grid-column: 2; grid-row: 1; }
  .mipDraftRow .btnConfirmDiagnosis { width: 100%; }
}
</style>
